/**
 * Service dédié pour le module Carte V2
 * Utilise les endpoints API réels du backend MS_Client
 * PRODUCTION READY - NO MOCK DATA
 * Refactorisé pour utiliser axios avec Bearer token automatique
 */

import axios from 'axios';

// Configuration des headers avec Bearer token
const headers = {
    'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userDetails'))?.token,
    'Accept-Language': localStorage.getItem('currentLanguage')
};

// Création de l'instance axios pour MS_Client
const ClientServiceRequest = axios.create({
    baseURL: process.env.REACT_APP_BASEURL_CLIENT_BACKEND,
    headers: headers
});

// Création de l'instance axios pour MS_Admin
const AdminServiceRequest = axios.create({
    baseURL: process.env.REACT_APP_BASEURL_ADMIN_BACKEND,
    headers: headers
});
// ===== GESTION DES TEMPLATES =====

/**
 * Récupérer tous les templates avec filtres
 */
export const getTemplates = (filters = {}) => {
    const params = {};
    if (filters.country) params.countryCode = filters.country;
    if (filters.type) params.cardType = filters.type;
    if (filters.category) params.category = filters.category;
    if (filters.isActive !== undefined) params.isActive = filters.isActive;

    return ClientServiceRequest.get('/Card/templates', { params })
        .then(response => Array.isArray(response.data) ? response.data : []);
};

/**
 * Récupérer un template par ID
 */
export const getTemplate = (templateId) => ClientServiceRequest.get(`/Card/templates/${templateId}`);

/**
 * Créer un nouveau template
 */
export const createTemplate = (templateData) => ClientServiceRequest.post('/Card/templates', templateData);

/**
 * Mettre à jour un template
 */
export const updateTemplate = (templateId, templateData) => ClientServiceRequest.put(`/Card/templates/${templateId}`, templateData);

/**
 * Supprimer un template
 */
export const deleteTemplate = (templateId) => ClientServiceRequest.delete(`/Card/templates/${templateId}`);

/**
 * Générer une prévisualisation de template
 */
export const generateTemplatePreview = (previewData) => ClientServiceRequest.post('/Card/templates/preview', previewData);

// ===== GÉNÉRATION DE CARTES =====

/**
 * Initier la production d'une carte individuelle
 */
export const generateIndividualCard = (cardData) => {
    const payload = {
        beneficiaryId: parseInt(cardData.beneficiaryId),
        countryCode: cardData.countryCode,
        templateId: cardData.templateId ? parseInt(cardData.templateId) : null,
        priority: cardData.priority || 'Normal',
        requestedBy: cardData.requestedBy,
        photoPath: cardData.photoPath,
        paymentReference: cardData.paymentReference,
        customData: cardData.customData
    };
    return ClientServiceRequest.post('/Card/initiate-production', payload);
};

/**
 * Générer un lot de cartes (batch) - Utilise POST /api/card/batch/create
 * Ce endpoint crée automatiquement les cartes pour chaque bénéficiaire
 */
export const generateBatchCards = (batchData) => {
    const payload = {
        clientIds: batchData.beneficiaryIds || batchData.clientIds,
        countryCode: batchData.countryCode,
        templateId: batchData.templateId || null,
        generateCards: true,
        operatorName: batchData.requestedBy || batchData.operatorName || 'System',
        notes: batchData.description || batchData.notes || `Batch: ${batchData.batchName || ''}`
    };

    return ClientServiceRequest.post('/Card/batch/create', payload)
        .then(response => {
            const result = response.data;
            const isSuccess = result.isSuccess !== undefined ? result.isSuccess : result.IsSuccess !== undefined ? result.IsSuccess : true;

            if (!isSuccess) {
                const error = new Error(result.message || result.Message || 'Error in batch creation');
                error.response = { data: result };
                throw error;
            }

            return result;
        });
};

/**
 * Récupérer les lots de cartes
 */
export const getCardBatches = (filters = {}) => {
    const params = {};
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.pageNumber) params.pageNumber = filters.pageNumber;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.status) params.status = filters.status;

    return ClientServiceRequest.get('/Card/batches', { params })
        .then(response => Array.isArray(response.data) ? response.data : [])
        .catch(error => {
            console.warn('Erreur lors de la récupération des lots, retour liste vide:', error);
            return [];
        });
};

// ===== GESTION DES CARTES =====

/**
 * Rechercher des cartes avec filtres avancés
 */
export const searchCards = (filters = {}) => {
    const params = {};
    if (filters.page) params.page = filters.page;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.searchTerm) params.searchTerm = filters.searchTerm;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortDirection) params.sortDirection = filters.sortDirection;
    if (filters.status) params.status = filters.status;
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.beneficiaryId) params.beneficiaryId = filters.beneficiaryId;

    return ClientServiceRequest.get('/Card/list', { params });
};

/**
 * Lister toutes les cartes avec filtres (alias de searchCards)
 */
export const listCards = (filters = {}) => searchCards(filters);

/**
 * Lister tous les lots (alias de getCardBatches)
 */
export const listBatches = (filters = {}) => getCardBatches(filters);

/**
 * Récupérer les cartes imprimées
 */
export const getPrintedCards = (filters = {}) => getPrintedCardsByPeriod(filters);

/**
 * Récupérer les détails d'une carte
 */
export const getCardDetails = (cardId) => ClientServiceRequest.get(`/Card/details/${cardId}`);

/**
 * Récupérer les cartes par statut
 */
export const getCardsByStatus = (status, page = 1, pageSize = 50) => {
    const params = {
        pageNumber: page,
        pageSize: pageSize
    };
    return ClientServiceRequest.get(`/Card/status/${status}`, { params })
        .then(response => Array.isArray(response.data) ? response.data : []);
};

// ===== STATISTIQUES ET ANALYTICS =====

/**
 * Récupérer les statistiques de production
 */
export const getProductionStatistics = (filters = {}) => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.countryCode) params.countryCode = filters.countryCode;

    return ClientServiceRequest.get('/Card/production-statistics', { params });
};

/**
 * Récupérer les statistiques des cartes par statut
 */
export const getCardStatistics = async () => {
    const statuses = [
        'DataValidation', 'PhotoProcessing', 'QualityControl',
        'Printing', 'Shipped', 'Delivered', 'Blocked', 'Cancelled'
    ];

    const statisticsPromises = statuses.map(async (status) => {
        try {
            const cards = await getCardsByStatus(status, 1, 1000);
            return { status, count: cards.length };
        } catch (error) {
            console.warn(`Erreur pour le statut ${status}:`, error);
            return { status, count: 0 };
        }
    });

    const results = await Promise.all(statisticsPromises);

    return results.reduce((acc, result) => {
        acc[result.status] = result.count;
        return acc;
    }, {});
};

/**
 * Récupérer les anomalies de cartes
 */
export const getCardAnomalies = (filters = {}) => {
    const params = {};
    if (filters.pageNumber) params.pageNumber = filters.pageNumber;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.severity) params.severity = filters.severity;

    return ClientServiceRequest.get('/Card/anomalies', { params });
};

// ===== GESTION DU CYCLE DE VIE =====

/**
 * Mettre à jour le statut d'une carte
 */
export const updateCardStatus = (cardId, statusData) => {
    const payload = {
        status: statusData.status,
        reason: statusData.reason,
        updatedBy: statusData.updatedBy || 'admin@company.com'
    };
    return ClientServiceRequest.put(`/Card/${cardId}/status`, payload);
};

/**
 * Bloquer une carte
 */
export const blockCard = (cardId, blockData) => {
    const payload = {
        reason: blockData.reason,
        isTemporary: blockData.isTemporary || false,
        expiryDate: blockData.expiryDate,
        comment: blockData.comment,
        blockedBy: blockData.blockedBy || 'admin@company.com'
    };
    return ClientServiceRequest.post(`/Card/${cardId}/block`, payload);
};

/**
 * Débloquer une carte
 */
export const unblockCard = (cardId, unblockData) => {
    const payload = {
        reason: unblockData.reason,
        comment: unblockData.comment,
        unblockedBy: unblockData.unblockedBy || 'admin@company.com'
    };
    return ClientServiceRequest.post(`/Card/${cardId}/unblock`, payload);
};

/**
 * Activer une carte
 */
export const activateCard = (cardId, activationData) => {
    const payload = {
        activationCode: activationData.activationCode || `ACT-${Date.now()}`,
        activatedBy: activationData.activatedBy || 'admin@company.com'
    };
    return ClientServiceRequest.post(`/Card/${cardId}/activate`, payload);
};

// ===== IMPRESSION ET PRODUCTION =====

/**
 * Récupérer le statut de production d'une carte
 */
export const getProductionStatus = (cardId) => ClientServiceRequest.get(`/Card/production-status/${cardId}`);

/**
 * Lancer l'impression par lot
 */
export const batchPrintCards = (batchData) => ClientServiceRequest.post('/api/card/Templates/batch-print', batchData);

/**
 * Génération PDF par lot
 */
export const batchPrintCardsPdf = (batchData) => {
    const batchId = batchData.batchId || batchData.id;
    if (!batchId) {
        throw new Error('Batch ID is required');
    }

    return ClientServiceRequest.get(`/Card/batch/${batchId}/pdf`, { responseType: 'blob' })
        .then(response => response.data);
};

/**
 * Suivre le statut d'un job d'impression
 */
export const getPrintJobStatus = (jobId) => ClientServiceRequest.get(`/api/card/Templates/print-jobs/${jobId}/status`);

// ===== EXPORT ET RAPPORTS =====

/**
 * Exporter les données de cartes
 */
export const exportCardsData = (filters = {}, format = 'excel') => {
    const params = { format, ...filters };
    return ClientServiceRequest.get('/Cards/export', { params, responseType: 'blob' })
        .then(response => response.data);
};

/**
 * Générer un rapport de traçabilité
 */
export const exportTraceabilityReport = (filters = {}) => {
    const params = { ...filters };
    return ClientServiceRequest.get('/Card/traceability-report', { params, responseType: 'blob' })
        .then(response => response.data);
};

// ===== RECHERCHE DE BÉNÉFICIAIRES =====

/**
 * Rechercher des bénéficiaires pour la génération de cartes
 */
export const searchBeneficiaries = (searchTerm, filters = {}) => {
    const params = {};
    if (searchTerm) params.searchTerm = searchTerm;
    if (filters.page) params.page = filters.page;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.country) params.country = filters.country;
    if (filters.police) params.police = filters.police;
    if (filters.famille) params.famille = filters.famille;
    if (filters.region) params.region = filters.region;
    if (filters.status) params.status = filters.status;

    return ClientServiceRequest.get('/BeneficiaryProfile/list', { params });
};

/**
 * Rechercher des bénéficiaires par critères avancés
 */
export const searchBeneficiariesByCriteria = (criteria) => {
    const params = {
        Page: criteria.page || 1,
        Limit: criteria.limit || 100
    };

    if (criteria.lastName || criteria.nom) {
        params.Search = criteria.lastName || criteria.nom;
    }

    if (criteria.police) {
        params.PolicyNumber = criteria.police;
    }

    if (criteria.famille) {
        params.FamilyId = criteria.famille;
    }

    if (criteria.status) {
        params.Status = criteria.status;
    }

    if (criteria.countryCode) {
        params.CountryCode = criteria.countryCode;
    }

    console.log('Paramètres de recherche envoyés:', params);

    return ClientServiceRequest.get('/BeneficiaryProfile/list', { params });
};

/**
 * Obtenir les détails d'un bénéficiaire
 */
export const getBeneficiaryDetails = (beneficiaryId) => ClientServiceRequest.get(`/BeneficiaryProfile/${beneficiaryId}`);

/**
 * Récupérer les cartes existantes d'un bénéficiaire
 */
export const getBeneficiaryCards = (beneficiaryId, filters = {}) => {
    const params = {
        beneficiaryId: beneficiaryId,
        pageSize: filters.pageSize || 50,
        page: filters.page || 1
    };
    return ClientServiceRequest.get(`/Card/beneficiary/${beneficiaryId}`, { params });
};

/**
 * Vérifier si le bénéficiaire a déjà une carte active
 */
export const checkExistingCards = async (beneficiaryId) => {
    try {
        const cardsResponse = await getBeneficiaryCards(beneficiaryId, { pageSize: 10 });
        const cardsData = cardsResponse.data || cardsResponse;
        const activeCards = cardsData.Data ? cardsData.Data.filter(card =>
            ['Active', 'InProduction', 'Pending', 'DataValidation', 'PhotoProcessing',
             'TemplateApplication', 'QualityControl', 'Printing', 'Packaging'].includes(card.Status)
        ) : [];

        return {
            hasActiveCard: activeCards.length > 0,
            activeCards: activeCards,
            totalCards: cardsData.Data ? cardsData.Data.length : 0,
            requiresPayment: activeCards.length > 0
        };
    } catch (error) {
        console.error('Erreur lors de la vérification des cartes existantes:', error);
        return {
            hasActiveCard: false,
            activeCards: [],
            totalCards: 0,
            requiresPayment: false
        };
    }
};

/**
 * Créer un enregistrement de paiement pour une carte supplémentaire
 */
export const createCardPayment = (paymentData) => {
    const payload = {
        beneficiaryId: paymentData.beneficiaryId,
        amount: paymentData.amount,
        currency: paymentData.currency || 'XAF',
        paymentMethod: paymentData.paymentMethod,
        description: `Paiement pour carte supplémentaire - Bénéficiaire ${paymentData.beneficiaryId}`,
        reference: paymentData.reference,
        paymentType: 'AdditionalCard'
    };
    return ClientServiceRequest.post('/api/Payment/card-payment', payload);
};

/**
 * Vérifier le statut d'un paiement
 */
export const verifyPayment = (paymentId) => ClientServiceRequest.get(`/api/Payment/${paymentId}/verify`);

/**
 * Récupérer la prévisualisation d'un template avec données (CardPreviewController)
 */
export const getTemplatePreviewUrl = async (templateId, previewData = null, side = 'Front') => {
    if (!previewData) {
        throw new Error('Preview data is required for card preview');
    }

    let photoBytes = null;
    if (previewData.photoBase64) {
        photoBytes = previewData.photoBase64;
    }

    const request = {
        templateId: templateId.toString(),
        cardData: {
            id: (previewData.cardNumber || '').toString(),
            name: (previewData.lastName || '').toString(),
            firstName: (previewData.firstName || '').toString(),
            birthDate: previewData.birthDate || new Date().toISOString(),
            idNumber: (previewData.cardNumber || '').toString(),
            nationality: (previewData.nationality || 'Ivoirienne').toString(),
            signature: previewData.signature ? previewData.signature.toString() : '',
            expiryDate: previewData.expiryDate || new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString(),
            customFields: {
                ...(previewData.customFields || {}),
                PhotoUrl: photoBytes || null
            },
            serialNumber: previewData.serialNumber ? previewData.serialNumber.toString() : '',
            policeNumber: previewData.policyNumber ? previewData.policyNumber.toString() : '',
            souscripteur: previewData.souscripteur ? previewData.souscripteur.toString() : '',
            familyId: previewData.familyId ? previewData.familyId.toString() : '',
            beneficiaryId: previewData.beneficiaryId ? previewData.beneficiaryId.toString() : '',
            insuranceCompany: (previewData.insuranceCompany || 'Acare').toString(),
            policyType: (previewData.policyType || 'Standard').toString()
        },
        options: {
            quality: 'Medium',
            showCutLines: false,
            showSafeZones: false,
            showBleedArea: false,
            highlightFields: false,
            background: 'White',
            scaleFactor: 1.0,
            watermarkEnabled: true,
            watermarkText: 'APERÇU'
        },
        clientId: previewData.beneficiaryId || null,
        side: side || 'Front'
    };

    try {
        const response = await ClientServiceRequest.post('/api/cards/preview/card/image', request, { responseType: 'blob' });
        return URL.createObjectURL(response.data);
    } catch (error) {
        console.error('Erreur dans getTemplatePreviewUrl:', error);
        throw error;
    }
};

// ===== ENDPOINTS MANQUANTS - PRODUCTION AVANCÉE =====

/**
 * Mettre à jour le statut d'une étape de production
 */
export const updateProductionStepStatus = (stepId, statusData) => {
    const payload = {
        status: statusData.status,
        notes: statusData.notes,
        updatedBy: statusData.updatedBy || 'admin@company.com'
    };
    return ClientServiceRequest.put(`/Card/production-step/${stepId}/status`, payload);
};

/**
 * Relancer une étape de production échouée
 */
export const retryProductionStep = (stepId, retryReason) => {
    const payload = {
        retryReason: retryReason || 'Retry demandé par l\'utilisateur'
    };
    return ClientServiceRequest.post(`/Card/production-step/${stepId}/retry`, payload);
};

// ===== GESTION AVANCÉE DES BATCHES =====

/**
 * Récupérer les détails d'un lot spécifique
 */
export const getCardBatchDetails = (batchId) => ClientServiceRequest.get(`/Card/batches/${batchId}`);

/**
 * Récupérer toutes les cartes d'un batch avec les informations des bénéficiaires
 */
export const getBatchCards = async (batchId, filters = {}) => {
    try {
        const response = await ClientServiceRequest.get(`/Card/batches/${batchId}`);
        const batch = response.data;

        const cards = batch.cards || batch.Cards || [];

        let filteredCards = cards;
        if (filters.status) {
            filteredCards = cards.filter(card => card.status === filters.status || card.Status === filters.status);
        }

        return {
            data: filteredCards,
            total: filteredCards.length
        };
    } catch (error) {
        console.error('Erreur dans getBatchCards:', error);
        throw error;
    }
};

/**
 * Mettre à jour le statut d'une carte dans un batch
 */
export const updateBatchCardStatus = (batchId, cardId, statusData) => {
    const payload = {
        status: statusData.status,
        reason: statusData.reason,
        notes: statusData.notes,
        updatedBy: statusData.updatedBy || localStorage.getItem('userEmail') || 'admin@company.com'
    };
    return ClientServiceRequest.put(`/Card/batches/${batchId}/cards/${cardId}/status`, payload);
};

/**
 * Mettre à jour le statut de plusieurs cartes dans un batch
 */
export const updateBatchCardsStatus = (batchId, cardIds, statusData) => {
    const payload = {
        cardIds: cardIds,
        status: statusData.status,
        reason: statusData.reason,
        notes: statusData.notes,
        updatedBy: statusData.updatedBy || localStorage.getItem('userEmail') || 'admin@company.com'
    };
    return ClientServiceRequest.put(`/Card/batches/${batchId}/cards/bulk-status`, payload);
};

/**
 * Récupérer toutes les cartes imprimées par période avec numéros de série
 */
export const getPrintedCardsByPeriod = (filters = {}) => {
    const params = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 50,
        sortBy: filters.sortBy || 'PrintedDate',
        sortDirection: filters.sortDirection || 'desc'
    };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.status) params.status = filters.status;
    if (filters.batchId) params.batchId = filters.batchId;

    return ClientServiceRequest.get('/Card/printed', { params });
};

/**
 * Exporter les cartes imprimées par période en Excel/CSV
 */
export const exportPrintedCardsByPeriod = async (filters = {}, format = 'excel') => {
    const params = { format };

    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.status) params.status = filters.status;
    if (filters.batchId) params.batchId = filters.batchId;

    try {
        const response = await ClientServiceRequest.get('/Card/printed/export', { params, responseType: 'blob' });
        const blob = response.data;

        const fileName = `cartes_imprimees_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : format === 'csv' ? 'csv' : 'pdf'}`;
        downloadFile(blob, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Erreur dans exportPrintedCardsByPeriod:', error);
        throw error;
    }
};

/**
 * Ajouter une carte à un lot existant
 */
export const addCardToBatch = (batchId, cardData) => {
    const payload = {
        cardId: cardData.cardId,
        addedBy: cardData.addedBy || 'admin@company.com'
    };
    return ClientServiceRequest.post(`/Card/batches/${batchId}/cards`, payload);
};

/**
 * Retirer une carte d'un lot
 */
export const removeCardFromBatch = (batchId, cardId, removalData) => {
    const payload = {
        removedBy: removalData.removedBy || 'admin@company.com',
        reason: removalData.reason || 'Retrait demandé'
    };
    return ClientServiceRequest.delete(`/Card/batches/${batchId}/cards/${cardId}`, { data: payload });
};

/**
 * Récupérer les cartes prêtes pour mise en lot
 */
export const getCardsReadyForBatching = (countryCode) => {
    return ClientServiceRequest.get(`/Card/ready-for-batching?countryCode=${countryCode}`)
        .then(response => Array.isArray(response.data) ? response.data : [])
        .catch(error => {
            console.error('Erreur dans getCardsReadyForBatching:', error);
            return [];
        });
};

// ===== HISTORIQUE & TRAÇABILITÉ =====

/**
 * Récupérer l'historique paginé d'une carte
 */
export const getCardHistory = (cardId, filters = {}) => {
    const params = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 10
    };
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;

    return ClientServiceRequest.get(`/Card/${cardId}/history`, { params });
};

// ===== ANALYTICS & STATISTIQUES AVANCÉES =====

/**
 * Récupérer les statistiques de production détaillées
 */
export const getProductionStatisticsDetailed = (filters = {}) => {
    const params = {};
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    return ClientServiceRequest.get('/Card/statistics/production', { params });
};

/**
 * Exporter le rapport de traçabilité
 */
export const exportTraceabilityReportAdvanced = async (filters = {}) => {
    const params = {
        format: filters.format || 'excel'
    };

    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;

    try {
        const response = await ClientServiceRequest.get('/Card/export', { params, responseType: 'blob' });
        const blob = response.data;

        const fileName = `traceability_report_${Date.now()}.${filters.format === 'excel' ? 'xlsx' : filters.format}`;
        downloadFile(blob, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Erreur dans exportTraceabilityReportAdvanced:', error);
        throw error;
    }
};

/**
 * Récupérer les anomalies paginées
 */
export const getCardAnomaliesPaginated = (filters = {}) => {
    const params = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 20
    };

    if (filters.severity) params.severity = filters.severity;
    if (filters.type) params.type = filters.type;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;

    return ClientServiceRequest.get('/Card/anomalies', { params });
};

/**
 * Récupérer les contrôles qualité d'une carte
 */
export const getCardQualityChecks = (cardId) => {
    return ClientServiceRequest.get(`/Card/${cardId}/quality-checks`)
        .then(response => Array.isArray(response.data) ? response.data : [])
        .catch(error => {
            console.error('Erreur dans getCardQualityChecks:', error);
            return [];
        });
};

/**
 * Recherche avancée avec tous les paramètres backend
 */
export const searchCardsAdvanced = (filters = {}) => {
    const params = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 20,
        sortBy: filters.sortBy || 'ProductionDate',
        sortDirection: filters.sortDirection || 'desc'
    };

    if (filters.searchTerm) params.searchTerm = filters.searchTerm;
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.status) params.status = filters.status;
    if (filters.beneficiaryId) params.beneficiaryId = filters.beneficiaryId;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;

    return ClientServiceRequest.get('/Card/list', { params });
};

// ===== RECHERCHE ASSURÉ ET GESTION DUPLICATAS =====

/**
 * Rechercher un assuré avec toutes ses cartes
 */
export const searchBeneficiaryWithCards = (searchType, searchTerm) => {
    const params = {
        searchType,
        searchTerm
    };
    return ClientServiceRequest.get('/Card/beneficiary-search', { params });
};

/**
 * Créer un duplicata de carte avec paiement
 */
export const createDuplicate = (cardId, duplicateData) => {
    const payload = {
        reason: duplicateData.reason,
        payment: {
            amount: duplicateData.payment.amount,
            paymentMethod: duplicateData.payment.paymentMethod,
            paymentReference: duplicateData.payment.paymentReference,
            paymentDate: duplicateData.payment.paymentDate,
            paidBy: duplicateData.payment.paidBy
        },
        requestedBy: duplicateData.requestedBy
    };
    return ClientServiceRequest.post(`/Card/${cardId}/duplicate`, payload);
};

/**
 * Récupérer l'historique des duplicatas d'une carte
 */
export const getDuplicateHistory = (cardId) => ClientServiceRequest.get(`/Card/${cardId}/duplicates`);

// ===== SERIAL RANGE MANAGEMENT =====

/**
 * Get all serial ranges
 */
export const getSerialRanges = (filters = {}) => {
    const params = {};
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.isActive !== undefined) params.isActive = filters.isActive;

    return ClientServiceRequest.get('/Card/serial-ranges', { params });
};

/**
 * Create a new serial range
 */
export const createSerialRange = (rangeData) => ClientServiceRequest.post('/Card/serial-ranges', rangeData);

// ===== ALERTS MANAGEMENT =====

/**
 * Get system alerts
 */
export const getAlerts = (filters = {}) => {
    const params = {};
    if (filters.type) params.type = filters.type;
    if (filters.priority) params.priority = filters.priority;
    if (filters.isRead !== undefined) params.isRead = filters.isRead;
    if (filters.limit) params.limit = filters.limit;

    return ClientServiceRequest.get('/Card/alerts', { params });
};

/**
 * Mark alert as read
 */
export const markAlertAsRead = (alertId) => ClientServiceRequest.put(`/Card/alerts/${alertId}/mark-read`);

// ===== PHYSICAL STOCK MANAGEMENT =====

/**
 * Get all stock items
 */
export const getStockItems = (filters = {}) => {
    const params = {};
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.lowStockOnly !== undefined) params.lowStockOnly = filters.lowStockOnly;

    return ClientServiceRequest.get('/Card/stock/items', { params });
};

/**
 * Create a new stock item
 */
export const createStockItem = (stockData) => ClientServiceRequest.post('/Card/stock/items', stockData);

/**
 * Restock an item
 */
export const restockItem = (stockId, restockData) => ClientServiceRequest.post(`/Card/stock/items/${stockId}/restock`, restockData);

/**
 * Get stock dashboard
 */
export const getStockDashboard = (countryCode = null) => {
    const params = {};
    if (countryCode) params.countryCode = countryCode;

    return ClientServiceRequest.get('/Card/stock/dashboard', { params });
};

/**
 * Récupérer la liste des stocks disponibles
 */
export const getAvailableStocks = () => {
    return ClientServiceRequest.get('/Stock/summary')
        .then(response => {
            const stocks = response.data;
            console.log('Raw stocks from API:', stocks);

            const filteredStocks = stocks.filter(stock => {
                const quantity = stock.currentQuantity || stock.CurrentQuantity ||
                                stock.quantityAvailable || stock.QuantityAvailable || 0;
                return quantity > 0;
            });

            console.log('Filtered stocks:', filteredStocks);
            return filteredStocks;
        });
};

/**
 * Créer un nouveau bénéficiaire via enrollment individual
 */
export const createBeneficiaryEnrollment = (enrollmentData) => {
    const payload = {
        beneficiaryId: enrollmentData.beneficiaryId || 0,
        countryCode: enrollmentData.countryCode,
        photoPath: enrollmentData.photoPath || "",
        photoBase64: enrollmentData.photoBase64 || "",
        createdBy: enrollmentData.createdBy || "",
        options: enrollmentData.options
    };
    return ClientServiceRequest.post('/Enrollment/individual', payload);
};

/**
 * Créer un batch d'enrollments avec photos
 */
export const createBatchEnrollments = (batchData) => {
    const payload = {
        batchName: batchData.batchName,
        countryCode: batchData.countryCode,
        enrollments: batchData.enrollments,
        createdBy: batchData.createdBy
    };
    return ClientServiceRequest.post('/Enrollment/batch', payload);
};

/**
 * Mettre à jour un profil bénéficiaire
 */
export const updateBeneficiaryProfile = (beneficiaryId, updateData) => {
    const payload = {
        firstName: updateData.firstName,
        lastName: updateData.lastName,
        email: updateData.email || "",
        sexe: updateData.sexe,
        birthdate: updateData.birthdate,
        numeroPolice: updateData.numeroPolice || "",
        numeroAssure: updateData.numeroAssure || "",
        souscripteur: updateData.souscripteur || "",
        assurePrincipalName: updateData.assurePrincipalName || "",
        numeroBeneficiairePrincipal: updateData.numeroBeneficiairePrincipal || "",
        lienParente: updateData.lienParente || "",
        clientPhoto: updateData.clientPhoto || ""
    };
    return ClientServiceRequest.put(`/BeneficiaryProfile/${beneficiaryId}`, payload);
};

/**
 * Rechercher un client par keyword
 */
export const searchClientByKeyword = (keyword) => {
    const encodedKeyword = encodeURIComponent(keyword.trim());
    return ClientServiceRequest.get(`/Client/search/${encodedKeyword}`);
};

/**
 * Récupérer les assurés principaux
 */
export const getPrincipalBeneficiaries = () => {
    return ClientServiceRequest.get('/Client/principal-beneficiaries');
};

/**
 * Récupérer les pays disponibles (depuis l'admin backend)
 */
/**
 * Récupérer les pays disponibles (depuis l'admin backend)
 */
export const getCountries = async () => {
    try {
        const response = await AdminServiceRequest.get('/Parameter/countries');
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Erreur dans getCountries:', error);
        throw error;
    }
};

/**
 * Initier la production d'une carte avec données détaillées
 */
export const initiateCardProductionDetailed = (cardData) => {
    const payload = {
        beneficiaryId: parseInt(cardData.beneficiaryId),
        countryCode: cardData.countryCode,
        templateId: cardData.templateId ? parseInt(cardData.templateId) : null,
        stockId: cardData.stockId ? parseInt(cardData.stockId) : null,
        priority: cardData.priority,
        requestedBy: cardData.requestedBy,
        photoPath: cardData.photoPath,
        paymentReference: cardData.paymentReference,
        customData: cardData.customData
    };
    return ClientServiceRequest.post('/Card/initiate-production', payload);
};

/**
 * Créer un nouveau batch de cartes avec template et stock
 */
export const createCardBatchWithTemplate = (batchData) => {
    const payload = {
        batchName: batchData.batchName,
        countryCode: batchData.countryCode,
        templateId: batchData.templateId ? parseInt(batchData.templateId) : null,
        expectedQuantity: batchData.expectedQuantity || 1,
        plannedStartDate: batchData.plannedStartDate,
        plannedEndDate: batchData.plannedEndDate,
        createdBy: batchData.createdBy,
        cardIds: batchData.cardIds?.map(id => parseInt(id)) || []
    };
    return ClientServiceRequest.post('/Card/batches', payload);
};

/**
 * Ajouter une carte à un batch existant (version détaillée)
 */
export const addCardToBatchDetailed = (batchId, cardData) => {
    const payload = {
        cardId: parseInt(cardData.cardId),
        addedBy: cardData.addedBy
    };
    return ClientServiceRequest.post(`/Card/batches/${batchId}/cards`, payload);
};

// ===== UTILITAIRES =====

/**
 * Télécharger un fichier blob
 */
export const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Formater le statut pour l'affichage
 */
export const formatStatus = (status) => {
    const statusMap = {
        'DataValidation': 'Validation données',
        'PhotoProcessing': 'Traitement photo',
        'TemplateApplication': 'Application template',
        'QualityControl': 'Contrôle qualité',
        'Printing': 'En impression',
        'Packaging': 'Emballage',
        'Shipped': 'Expédiée',
        'Delivered': 'Livrée',
        'Blocked': 'Bloquée',
        'Cancelled': 'Annulée'
    };
    return statusMap[status] || status;
};

/**
 * Configuration du badge de statut
 */
export const getStatusBadgeConfig = (status) => {
    const statusConfig = {
        'DataValidation': { severity: 'info', text: 'Validation données' },
        'PhotoProcessing': { severity: 'info', text: 'Traitement photo' },
        'TemplateApplication': { severity: 'info', text: 'Application template' },
        'QualityControl': { severity: 'warning', text: 'Contrôle qualité' },
        'Printing': { severity: 'success', text: 'Impression' },
        'Packaging': { severity: 'success', text: 'Emballage' },
        'Shipped': { severity: 'success', text: 'Expédiée' },
        'Delivered': { severity: 'success', text: 'Livrée' },
        'Blocked': { severity: 'danger', text: 'Bloquée' },
        'Cancelled': { severity: 'danger', text: 'Annulée' }
    };

    return statusConfig[status] || { severity: 'secondary', text: status };
};

/**
 * Valider un numéro de carte
 */
export const validateCardNumber = (cardNumber) => {
    return /^[0-9]{16}$/.test(cardNumber);
};

/**
 * Valider un ID bénéficiaire
 */
export const validateBeneficiaryId = (beneficiaryId) => {
    return /^[0-9]+$/.test(beneficiaryId);
};

// Export d'un objet avec toutes les fonctions pour compatibilité avec l'ancienne API
const CarteV2Service = {
    getTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    generateTemplatePreview,
    generateIndividualCard,
    generateBatchCards,
    getCardBatches,
    searchCards,
    listCards,
    listBatches,
    getPrintedCards,
    getCardDetails,
    getCardsByStatus,
    getProductionStatistics,
    getCardStatistics,
    getCardAnomalies,
    updateCardStatus,
    blockCard,
    unblockCard,
    activateCard,
    getProductionStatus,
    batchPrintCards,
    batchPrintCardsPdf,
    getPrintJobStatus,
    exportCardsData,
    exportTraceabilityReport,
    searchBeneficiaries,
    searchBeneficiariesByCriteria,
    getBeneficiaryDetails,
    getBeneficiaryCards,
    checkExistingCards,
    createCardPayment,
    verifyPayment,
    getTemplatePreviewUrl,
    updateProductionStepStatus,
    retryProductionStep,
    getCardBatchDetails,
    getBatchCards,
    updateBatchCardStatus,
    updateBatchCardsStatus,
    getPrintedCardsByPeriod,
    exportPrintedCardsByPeriod,
    addCardToBatch,
    removeCardFromBatch,
    getCardsReadyForBatching,
    getCardHistory,
    getProductionStatisticsDetailed,
    exportTraceabilityReportAdvanced,
    getCardAnomaliesPaginated,
    getCardQualityChecks,
    searchCardsAdvanced,
    searchBeneficiaryWithCards,
    createDuplicate,
    getDuplicateHistory,
    getSerialRanges,
    createSerialRange,
    getAlerts,
    markAlertAsRead,
    getStockItems,
    createStockItem,
    restockItem,
    getStockDashboard,
    getAvailableStocks,
    createBeneficiaryEnrollment,
    createBatchEnrollments,
    updateBeneficiaryProfile,
    searchClientByKeyword,
    getPrincipalBeneficiaries,
    getCountries,
    initiateCardProductionDetailed,
    createCardBatchWithTemplate,
    addCardToBatchDetailed,
    downloadFile,
    formatStatus,
    getStatusBadgeConfig,
    validateCardNumber,
    validateBeneficiaryId
};

export default CarteV2Service;
